import React, { useState, useEffect } from 'react'
import { Typography, IconButton, Tooltip, makeStyles } from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { useQuery } from 'react-query'
import { useTranslate } from 'react-admin'
import { USER_ADDONS } from '../../../utils/constants/userAddons'
import ORGANISATION_TYPES from '../../../utils/constants/OrganisationTypes'
import QueryKeys from '../../../utils/constants/queryKeys'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import localStorageIds from '../../../utils/constants/localStorageIds'
import getFederationStructure from '../../../Apis/competition/getFederationStructure'
import CompetitionFilter from '../../CmsAdminPage/CompetitionFilter/CompetitionFilter'
import SeasonFilter from '../../CmsAdminPage/SeasonFilter/SeasonFilter'
import widgetsSectionStyles from './widgetsSection.styles'

const buildSnippet = ({ widgetName, containerId, params }) => {
  const entries = Object.entries(params)
  const paramLines = [
    `    targetElement: "#${containerId}",`,
    ...entries.map(([k, v], i) => `    ${k}: "${v}"${i < entries.length - 1 ? ',' : ''}`),
  ]
  return [
    '<script src="https://cdn.mismatch.gr/public/widgets/stats.latest.min.js"></script>',
    `<div id="${containerId}"></div>`,
    '<script>',
    `  ${widgetName}({`,
    ...paramLines,
    '  });',
    '</script>',
  ].join('\n')
}

const WidgetSnippet = ({ title, code, classes, translate, controls }) => {
  const [copied, setCopied] = useState(false)

  const onCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className={classes.snippetWrapper}>
      <Typography variant='subtitle1' className={classes.widgetTitle}>
        {title}
      </Typography>
      {controls && <div className={classes.controls}>{controls}</div>}
      <div className={classes.codeBlockWrapper}>
        <pre className={classes.codeBlock}>{code}</pre>
        <Tooltip title={copied ? translate('ra.text.copied') : translate('ra.text.copy')}>
          <IconButton size='small' onClick={onCopy} className={classes.copyButton}>
            <FileCopyIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  )
}

const WidgetsSection = () => {
  const useStyles = makeStyles(widgetsSectionStyles)
  const classes = useStyles()
  const translate = useTranslate()
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_ORG]: selectedOrg } = localStorageValue || {}

  const isFederation = selectedOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION
  const hasStats = selectedOrg?.addons?.includes(USER_ADDONS.STATS)
  const internalOrgId = selectedOrg?.id
  const externalOrgId = selectedOrg?.externalId || 'YOUR_ORG_ID'

  const { data: federationStructure } = useQuery(
    [QueryKeys.GET_FEDERATION_STRUCTURE, internalOrgId],
    () => getFederationStructure(internalOrgId),
    { enabled: Boolean(isFederation && hasStats && internalOrgId) },
  )

  const [competitionWidgetComp, setCompetitionWidgetComp] = useState(null)
  const [competitionWidgetSeason, setCompetitionWidgetSeason] = useState(null)
  const [liveScoreWidgetComp, setLiveScoreWidgetComp] = useState(null)

  useEffect(() => {
    if (!federationStructure?.length) return
    const firstComp = federationStructure[0]
    if (!competitionWidgetComp) {
      setCompetitionWidgetComp(firstComp)
      setCompetitionWidgetSeason(firstComp.seasons?.[0] || null)
    }
    if (!liveScoreWidgetComp) {
      setLiveScoreWidgetComp(firstComp)
    }
  }, [federationStructure])

  const handleCompetitionWidgetCompChange = (comp) => {
    setCompetitionWidgetComp(comp)
    setCompetitionWidgetSeason(comp?.seasons?.[0] || null)
  }

  if (!isFederation) {
    return null
  }

  const competitionWidgetSeasons =
    federationStructure?.find((c) => c.id === competitionWidgetComp?.id)?.seasons ?? []

  const widgets = [
    {
      key: 'competition',
      title: translate('ra.text.competitionWidget'),
      controls: (
        <>
          <CompetitionFilter
            competitions={federationStructure}
            selectedCompetition={competitionWidgetComp}
            onCompetitionChange={handleCompetitionWidgetCompChange}
          />
          {competitionWidgetComp && (
            <SeasonFilter
              seasons={competitionWidgetSeasons}
              selectedSeason={competitionWidgetSeason}
              onSeasonChange={setCompetitionWidgetSeason}
            />
          )}
        </>
      ),
      code: buildSnippet({
        widgetName: 'Competition',
        containerId: 'msm-competition-widget',
        params: {
          orgId: externalOrgId,
          compId: competitionWidgetComp?.id || 'YOUR_COMPETITION_ID',
          seasonId: competitionWidgetSeason?.id || 'YOUR_SEASON_ID',
        },
      }),
    },
    {
      key: 'boxscore',
      title: translate('ra.text.boxscoreWidget'),
      code: buildSnippet({
        widgetName: 'BoxScore',
        containerId: 'msm-boxscore-widget',
        params: {
          orgId: externalOrgId,
          gameId: 'YOUR_GAME_ID',
        },
      }),
    },
    {
      key: 'competitionLiveScore',
      title: translate('ra.text.competitionLiveScoreWidget'),
      controls: (
        <CompetitionFilter
          competitions={federationStructure}
          selectedCompetition={liveScoreWidgetComp}
          onCompetitionChange={setLiveScoreWidgetComp}
        />
      ),
      code: buildSnippet({
        widgetName: 'CompetitionLiveScore',
        containerId: 'msm-competition-livescores-widget',
        params: {
          orgId: externalOrgId,
          compId: liveScoreWidgetComp?.id || 'YOUR_COMPETITION_ID',
        },
      }),
    },
  ]

  return (
    <div className={classes.section}>
      <Typography variant='h6' className={classes.sectionTitle}>
        {translate('ra.text.widgets')}
      </Typography>
      <Typography variant='body2' className={classes.sectionSubtitle}>
        {translate('ra.text.widgetsSubtitle')}{' '}
        <a
          href='https://docs.mismatch.gr/docs/Stats/Widget_Setup'
          target='_blank'
          rel='noopener noreferrer'
          className={classes.docsLink}
        >
          {translate('ra.text.widgetsDocsLink')}
        </a>
      </Typography>
      {!hasStats ? (
        <Typography className={classes.requireStatsMessage}>
          {translate('ra.text.widgetsRequireStats')}
        </Typography>
      ) : (
        widgets.map((w) => (
          <WidgetSnippet
            key={w.key}
            title={w.title}
            controls={w.controls}
            code={w.code}
            classes={classes}
            translate={translate}
          />
        ))
      )}
    </div>
  )
}

export default WidgetsSection
